import {ReactElement, useCallback, useState} from 'react'
import {kycStatuses} from '@bdswiss/common-enums'
import {useHistory} from 'react-router-dom'

import {Client} from '../../../graphql/types'
import {useFetchAddressProposal, useConfirmAddressProposal} from './hooks'
import {getIsAddressValidAccordingProposal} from './utils'
import {SubmitResultModal} from './SubmitResultModal'
import {ProposalAddressPopupContent} from './ProposalAddressPopupContent'

type ProposalAddressPopupProps = {
  viewer: Client
}

const PERSONAL_PROFILE_SETTINGS_URL = '/settings/profile/personal-profile/details'
const PROPOSAL_ADDRESS_VERIFICATION_FLAG_QUERY_PARAM = 'proposal_address_verification=true'

function ProposalAddressPopup(props: ProposalAddressPopupProps): ReactElement | null {
  const history = useHistory()

  const {viewer} = props
  const {kycStatus, metadata, address} = viewer

  const parsedMetadata = metadata ? JSON.parse(metadata) : {}
  const isWrongAddressTag = metadata ? parsedMetadata.isWrongAddressTag : false
  const isPopupShouldBeOpened = isWrongAddressTag && kycStatus && [kycStatuses.rejected.value, kycStatuses.pending.value].includes(kycStatus)
  const skipProposalAddressFetching = !isPopupShouldBeOpened || !viewer.id

  const {addressProposal, fetching: fetchingAddressProposal} = useFetchAddressProposal(viewer.id, {skip: skipProposalAddressFetching})
  const {confirmAddressProposal, submitting} = useConfirmAddressProposal(viewer.id!)

  const isAddressValidAccordingProposal = getIsAddressValidAccordingProposal({address, addressProposal})

  const [isOpen, setIsOpen] = useState(isPopupShouldBeOpened)
  const [isSubmitResultModalOpen, setIsSubmitResultModalOpen] = useState(false)

  const handleClose = useCallback(() => setIsOpen(false), [])
  const handleSubmitResultModalClose = useCallback(() => setIsSubmitResultModalOpen(false), [])

  const handleEditClick = useCallback(() => {
    handleClose()

    history.push({
      pathname: PERSONAL_PROFILE_SETTINGS_URL,
      search: PROPOSAL_ADDRESS_VERIFICATION_FLAG_QUERY_PARAM
    })
  }, [])

  const handleConfirm = useCallback(async () => {
    try {
      await confirmAddressProposal()

      setIsSubmitResultModalOpen(true)
      handleClose()
    } catch (e) {
      handleClose()
    }
  }, [])

  const isModalContentShown = viewer.id
    && isOpen
    && addressProposal
    && !fetchingAddressProposal
    && !isAddressValidAccordingProposal

  return (
    <>
      {isModalContentShown ? (
        <ProposalAddressPopupContent
          addressProposal={addressProposal}
          submitting={submitting}
          onEditClick={handleEditClick}
          onConfirmClick={handleConfirm}
          onClose={handleClose}
        />
      ) : null}
      <SubmitResultModal isOpen={isSubmitResultModalOpen} onClose={handleSubmitResultModalClose} />
    </>
  )
}

export {ProposalAddressPopup}
